import { mapActions, mapGetters } from 'vuex';
import { vehicleGroups, vehicleNumberGroups, vehicleTypes } from '@/features/selectors';

export default {
  computed: {
    ...mapGetters('vehicleModule', { isVerified: 'isVehicleDataVerified' }),
    getVehicleTypes() { return vehicleTypes; },
    getVehicleTemporaryNumberGroups() { return vehicleNumberGroups.temporary; },
    getVehiclePermanentNumberGroups() { return vehicleNumberGroups.permanent; },
    getAllVehicleNumberGroups() { return vehicleNumberGroups.all; },
  },
  methods: {
    ...mapActions('vehicleModule', ['getVehicleInfoByNumber']),
    getFilteredVehicleTypes(categories) {
      return this.getVehicleTypes?.map((vehicleType) => ({
        ...vehicleType,
        engines: vehicleType.engines.filter((engine) => categories.includes(engine.value)),
        types: vehicleType.types.filter((type) => categories.includes(type.value)),
      }))
        ?.filter((vehicleType) => vehicleType.engines.length > 0 || vehicleType.types.length > 0);
    },
    getVehicleTypeByTypeId(typeId) {
      if (!typeId) return null;

      for (const vehicleType of this.getVehicleTypes) {
        const foundInEngines = vehicleType.engines.find(engine => engine.value === typeId);
        if (foundInEngines) return vehicleType;

        const foundInTypes = vehicleType.types.find(type => type.value === typeId);
        if (foundInTypes) return vehicleType;
      }

      return null;
    },
    getVehicleTypeNameByTypeId(typeId) {
      if (!typeId) return null;

      for (const vehicle of this.getVehicleTypes) {
        const foundInEngines = vehicle.engines.find(engine => engine.value === typeId);
        if (foundInEngines) return vehicle.name;

        const foundInTypes = vehicle.types.find(type => type.value === typeId);
        if (foundInTypes) return vehicle.name;

        if (vehicle.external_id === typeId) {
          return vehicle.name;
        }
      }

      return null;
    },
    getVehicleTypeGroupByTypeId(typeId) {
      if (!typeId) return null;

      for (const vehicle of this.getVehicleTypes) {
        const foundInEngines = vehicle.engines.find(engine => engine.value === typeId);
        if (foundInEngines) return vehicle.group;

        const foundInTypes = vehicle.types.find(type => type.value === typeId);
        if (foundInTypes) return vehicle.group;

        if (vehicle.external_id === typeId) {
          return vehicle.group;
        }
      }

      return null;
    },
    getVehicleEngineCategoryNameByTypeId(typeId) {
      if (!typeId) return null;

      for (const vehicle of this.getVehicleTypes) {
        const foundInEngines = vehicle.engines.find(engine => engine.value === typeId);
        if (foundInEngines) return foundInEngines.name;

        const foundInTypes = vehicle.types.find(type => type.value === typeId);
        if (foundInTypes) return foundInTypes.name;
      }

      return null;
    },
    getVehicleExternalIdByTypeId(typeId) {
      for (const vehicle of this.getVehicleTypes) {
        const foundInEngines = vehicle.engines.find(engine => engine.value === typeId);
        if (foundInEngines) return vehicle.external_id;

        const foundInTypes = vehicle.types.find(type => type.value === typeId);
        if (foundInTypes) return vehicle.external_id;
      }

      return null;
    },
    getAvailableVehicleTypes(policyholderType, foreignRegistration) {
      if (policyholderType === 'beneficiary') {
        return this.getFilteredVehicleTypes(
          foreignRegistration ? ['A2', 'B1', 'B2', 'B3', 'B5'] : ['A1', 'A2', 'B1', 'B2', 'B3', 'B5']
        )?.map((vehicleType) => {
          if (this.isElectricCar(vehicleType)) {
            return {
              ...vehicleType,
              name: 'Легковий електромобіль (до 100 Кв)',
            };
          } else {
            return vehicleType;
          }
        });
      }

      return this.getVehicleTypes;
    },
    getAvailableVehicleCategories(policyholderType) {
      return this.getAvailableVehicleTypes(policyholderType).flatMap((vehicleType) => {
        const categories = [vehicleType?.external_id];

        if (vehicleType?.engines?.length > 0) {
          categories.push(...vehicleType.engines.map((engine) => engine.value));
        }

        if (vehicleType?.types?.length > 0) {
          categories.push(...vehicleType.types.map((type) => type.value));
        }

        return categories;
      });
    },
    getVehicleOptions(vehicleType) {
      if (vehicleType?.engines?.length) {
        return vehicleType?.engines;
      }

      if (vehicleType?.types?.length) {
        return vehicleType?.types;
      }

      return [];
    },
    getVehicleOptionsLabel(externalId) {
      const vehicleType = this.getVehicleTypes.find(type => type.external_id === externalId);

      return vehicleType?.engines?.length ? 'Об\'єм двигуна' : 'Тип';
    },
    getVehicleOptionsPlaceholder(vehicleType) {
      if (vehicleType?.engines?.length) {
        return vehicleType?.engines[0]?.name;
      }

      if (vehicleType?.types?.length) {
        return vehicleType?.types[0]?.name;
      }

      return '';
    },
    isVehicleDataVerified(vehicleData) {
      return this.isVerified(vehicleData);
    },
    isElectricCar(vehicleType) {
      if (!vehicleType) return false;
      return vehicleType.group === vehicleGroups.CAR && vehicleType.external_id === 'B5';
    },
    isTrailer(vehicleType) {
      if (!vehicleType) return false;
      return vehicleType.group === vehicleGroups.TRAILER;
    },
  },
};
